// DEBT-VITA™ — Template HTML offscreen pour le rapport PDF (12+ pages A4).
// Capture par html2canvas via [data-report-section] sur chaque <section>.
// Thème sombre : fond noir profond #0A0A0A, accents or #C9A84C.
import React from "react";

const COLORS = {
  black: "#0A0A0A",
  ink: "#0F1419",
  paper: "#FFFFFF",
  gold: "#C9A84C",
  red: "#C0392B",
  green: "#27AE60",
  orange: "#E67E22",
  yellow: "#F1C40F",
  blue: "#1A3C5E",
  ash: "#2C2C2C",
};

const ALERT_VARIANTS = {
  1: { label: "DETTE SOUTENABLE", color: COLORS.green, icon: "✓" },
  2: { label: "DETTE SOUS TENSION", color: COLORS.yellow, icon: "⚠" },
  3: { label: "DETTE CRITIQUE", color: COLORS.orange, icon: "▲" },
  4: { label: "DETTE INSOUTENABLE", color: COLORS.red, icon: "✗" },
};

function fmt(v, digits = 2) {
  if (v === null || v === undefined || Number.isNaN(v)) return "—";
  return Number(v).toLocaleString("fr-FR", {
    maximumFractionDigits: digits,
    minimumFractionDigits: 0,
  });
}
function fmtMoney(v, currency = "UM") {
  if (v === null || v === undefined) return "—";
  return `${fmt(v, 0)} ${currency}`;
}

// ----------------------------------------------------------------------------
// Common page wrapper (210×297 mm @ 96dpi → 794×1123 px)
// ----------------------------------------------------------------------------
function Page({ children, dark = false, footer, pageNum, totalPages, country, year }) {
  return (
    <section
      data-report-section
      style={{
        width: "794px",
        minHeight: "1123px",
        background: dark ? COLORS.black : COLORS.paper,
        color: dark ? COLORS.paper : COLORS.ink,
        padding: "44px 56px",
        fontFamily: "'Inter', 'Segoe UI', system-ui, sans-serif",
        fontSize: "12px",
        lineHeight: 1.5,
        boxSizing: "border-box",
        position: "relative",
        breakAfter: "page",
      }}
    >
      {/* Top gold band */}
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: "10px", background: COLORS.gold }} />
      <div style={{ minHeight: "1015px" }}>{children}</div>
      <div
        style={{
          position: "absolute",
          bottom: "20px",
          left: "56px",
          right: "56px",
          fontSize: "9px",
          color: dark ? "rgba(255,255,255,0.6)" : "#6b7280",
          display: "flex",
          justifyContent: "space-between",
          borderTop: dark ? "1px solid rgba(255,255,255,0.15)" : "1px solid #e5e7eb",
          paddingTop: "6px",
        }}
      >
        <span>
          VITAE-PUBLICA™ DEBT-VITA™ | {country || "—"} {year || ""} | ELY Mustapha
          PMP·I-PMP·IBM
        </span>
        <span>
          {footer || ""} {pageNum && totalPages ? `· Page ${pageNum}/${totalPages}` : ""}
        </span>
      </div>
    </section>
  );
}

// ----------------------------------------------------------------------------
// Page 1 — Cover
// ----------------------------------------------------------------------------
function CoverPage({ project, params, results, generatedAt, totalPages }) {
  const alert = ALERT_VARIANTS[results?.alert_level?.level] || ALERT_VARIANTS[1];
  const country = project?.country || params?.country || "Pays non spécifié";
  const year = params?.fiscal_year || project?.fiscal_year || "";
  return (
    <Page dark country={country} year={year} pageNum={1} totalPages={totalPages}>
      <div style={{ marginTop: "40px" }}>
        <p style={{ fontSize: "11px", letterSpacing: "0.4em", color: COLORS.gold, fontWeight: 700, margin: 0 }}>
          VITAE-PUBLICA™
        </p>
        <h1 style={{ fontSize: "46px", fontWeight: 900, color: COLORS.paper, margin: "16px 0 0", lineHeight: 1.05 }}>
          RAPPORT
          <br />
          DEBT-VITA™
        </h1>
        <p style={{ fontSize: "16px", color: "rgba(255,255,255,0.85)", marginTop: "16px", maxWidth: "540px" }}>
          Analyse du Poids de l'Endettement Public en Vies Humaines
        </p>
      </div>

      <div style={{ marginTop: "60px" }}>
        <p style={{ fontSize: "13px", color: "rgba(255,255,255,0.6)", margin: 0, letterSpacing: "0.2em" }}>
          PAYS / EXERCICE
        </p>
        <p style={{ fontSize: "26px", color: COLORS.paper, fontWeight: 700, marginTop: "6px" }}>
          {country} — {year}
        </p>
      </div>

      <div
        style={{
          marginTop: "80px",
          display: "inline-block",
          padding: "16px 32px",
          background: alert.color,
          color: "#fff",
          fontSize: "20px",
          fontWeight: 800,
          letterSpacing: "0.2em",
        }}
      >
        {alert.icon} {alert.label}
      </div>

      <div style={{ position: "absolute", bottom: "100px", left: "56px", right: "56px" }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px", color: "rgba(255,255,255,0.85)", fontSize: "11px" }}>
          <div>
            <p style={{ color: COLORS.gold, fontWeight: 700, letterSpacing: "0.2em", margin: 0 }}>
              MÉTHODOLOGIE
            </p>
            <p style={{ marginTop: "4px" }}>
              Nouvelle Théorie des Finances Publiques
              <br />
              Ahmed ELY Mustapha
            </p>
          </div>
          <div>
            <p style={{ color: COLORS.gold, fontWeight: 700, letterSpacing: "0.2em", margin: 0 }}>
              PROGRAMMEUR
            </p>
            <p style={{ marginTop: "4px" }}>
              ELY Mustapha
              <br />
              PMP · I-PMP · IBM Full Stack Developer
            </p>
          </div>
          <div>
            <p style={{ color: COLORS.gold, fontWeight: 700, letterSpacing: "0.2em", margin: 0 }}>
              GÉNÉRÉ LE
            </p>
            <p style={{ marginTop: "4px" }}>
              {new Date(generatedAt || Date.now()).toLocaleString("fr-FR")}
            </p>
          </div>
          <div>
            <p style={{ color: COLORS.gold, fontWeight: 700, letterSpacing: "0.2em", margin: 0 }}>
              RÉFÉRENCE
            </p>
            <p style={{ marginTop: "4px", fontSize: "9px", color: "rgba(255,255,255,0.55)" }}>
              {(project?.id || "").slice(0, 8)}
            </p>
          </div>
        </div>
      </div>
    </Page>
  );
}

// ----------------------------------------------------------------------------
// Page 2 — Avertissement & Méthodologie
// ----------------------------------------------------------------------------
const FORMULAS = [
  { id: "D1", name: "Dette par contribuable actif", expr: "DPC = Dette_totale / Population_active",
    expl: "Quote-part nominale de la dette publique par citoyen en âge de travailler." },
  { id: "D2", name: "Jours de vie hypothéqués", expr: "JVD = DPC / Salaire_journalier",
    expl: "Conversion vitaliste de DPC en jours de vie déjà engagés à servir la dette." },
  { id: "D3", name: "Années de vie hypothéquées", expr: "AVH = JVD / 365",
    expl: "Lecture annuelle du poids — utile pour la comparaison à la durée de carrière." },
  { id: "D4", name: "Part de carrière sacrifiée", expr: "PCS = (AVH / Durée_carrière) × 100",
    expl: "Pourcentage des années actives effectivement consacrées à la dette." },
  { id: "D5", name: "Service annuel en vies", expr: "SAV = Service_annuel / (Salaire_journalier × Pop_active)",
    expl: "Nombre de jours de vie consommés chaque année rien que pour servir la dette." },
  { id: "D6", name: "Charge intergénérationnelle", expr: "CEB(t) = Σ déficits actualisés / Pop_active(t)",
    expl: "Projection sur N années — combien de jours de vie un enfant né aujourd'hui héritera." },
  { id: "D7", name: "TAVD — Taux d'Aliénation Vitaliste", expr: "TAVD = (JVD / JVF) × 100",
    expl: "Ratio dette / fiscalité courante, exprimé en vies humaines (≥ 100 % = dette > pression fiscale annuelle)." },
];

function MethodologyPage({ country, year, totalPages }) {
  return (
    <Page country={country} year={year} pageNum={2} totalPages={totalPages}>
      <h2 style={{ fontSize: "26px", fontWeight: 900, margin: 0 }}>Avertissement & Méthodologie</h2>

      <div
        style={{
          marginTop: "20px",
          background: "#fff5f5",
          border: `2px solid ${COLORS.red}`,
          padding: "18px 22px",
          borderRadius: "8px",
        }}
      >
        <p style={{ fontWeight: 800, color: COLORS.red, fontSize: "12px", letterSpacing: "0.1em", margin: 0 }}>
          ⚠ AVERTISSEMENT MÉTHODOLOGIQUE
        </p>
        <p style={{ marginTop: "8px", color: "#3a1a1a" }}>
          Ce rapport traduit des données financières publiques en équivalents
          de <strong>vies humaines</strong> selon la <em>Nouvelle Théorie des
          Finances Publiques</em> d'Ahmed ELY Mustapha. Il constitue un outil
          de sensibilisation démocratique, <strong>non un document comptable
          officiel ni un avis juridique opposable</strong>. Les conclusions
          tirées doivent être confrontées à une expertise économique et
          juridique humaine.
        </p>
      </div>

      <h3 style={{ fontSize: "16px", marginTop: "32px", fontWeight: 800 }}>Les 7 formules vitalistes de la dette</h3>
      <div style={{ marginTop: "12px" }}>
        {FORMULAS.map((f) => (
          <div
            key={f.id}
            style={{ borderTop: "1px solid #e5e7eb", padding: "10px 0", display: "grid", gridTemplateColumns: "60px 1fr", gap: "12px" }}
          >
            <div style={{ fontWeight: 900, color: COLORS.gold, fontSize: "14px" }}>{f.id}</div>
            <div>
              <p style={{ fontWeight: 800, margin: 0 }}>{f.name}</p>
              <p style={{ fontFamily: "ui-monospace, monospace", color: COLORS.blue, marginTop: "2px", fontSize: "11px" }}>
                {f.expr}
              </p>
              <p style={{ marginTop: "4px", color: "#444" }}>{f.expl}</p>
            </div>
          </div>
        ))}
      </div>
    </Page>
  );
}

// ----------------------------------------------------------------------------
// Page 3 — Synthèse exécutive (6 KPIs)
// ----------------------------------------------------------------------------
function ExecSummaryPage({ results, params, country, year, totalPages }) {
  const currency = params?.currency || "UM";
  const horizonYear = new Date().getFullYear() + (params?.projection_years || 30);
  const alert = ALERT_VARIANTS[results?.alert_level?.level] || ALERT_VARIANTS[1];
  const tavd = results?.tavd;

  const kpis = [
    { label: "Dette par contribuable", value: fmtMoney(results?.dette_par_tete_um, currency),
      sub: results?.jours_vie_dette ? `${fmt(results.jours_vie_dette, 1)} jours de vie hypothéqués` : "—" },
    { label: "Années de vie hypothéquées", value: results?.annees_vie_dette ? `${fmt(results.annees_vie_dette, 2)} ans` : "—",
      sub: results?.pct_carriere_dette ? `${fmt(results.pct_carriere_dette, 2)} % de la carrière` : "—" },
    { label: "Service annuel en jours", value: results?.jours_vie_service_annuel ? `${fmt(results.jours_vie_service_annuel, 1)} j/an` : "—",
      sub: results?.jours_vie_interets !== null && results?.jours_vie_capital !== null ? `${fmt(results.jours_vie_interets, 1)} j intérêts + ${fmt(results.jours_vie_capital, 1)} j capital` : "—" },
    { label: "Charge enfant né aujourd'hui", value: results?.annees_vie_enfant ? `${fmt(results.annees_vie_enfant, 2)} ans` : "—",
      sub: `à l'horizon ${horizonYear}` },
    { label: "Dette nationale en vies", value: results?.dette_vie_nationale_totale ? `${fmt(results.dette_vie_nationale_totale / 1e9, 2)} Md j` : "—",
      sub: results?.annees_vie_collectives ? `${fmt(results.annees_vie_collectives / 1e6, 2)} M années collectives` : "—" },
    { label: "TAVD", value: tavd !== null && tavd !== undefined ? `${fmt(tavd, 1)} %` : "—",
      sub: tavd ? `${fmt(tavd / 100, 2)}× la pression fiscale annuelle` : "—" },
  ];

  return (
    <Page country={country} year={year} pageNum={3} totalPages={totalPages}>
      <h2 style={{ fontSize: "26px", fontWeight: 900, margin: 0 }}>Synthèse Exécutive</h2>
      <p style={{ marginTop: "8px", color: "#555" }}>
        Six indicateurs vitalistes pour saisir d'un coup d'œil le poids de la
        dette publique en vies humaines.
      </p>

      <div style={{ marginTop: "20px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
        {kpis.map((k, i) => (
          <div
            key={i}
            style={{ border: "1px solid #e5e7eb", borderTop: `4px solid ${COLORS.gold}`, padding: "14px 16px", borderRadius: "6px" }}
          >
            <p style={{ fontSize: "10px", letterSpacing: "0.2em", color: "#6b7280", fontWeight: 700, margin: 0 }}>
              {k.label.toUpperCase()}
            </p>
            <p style={{ fontSize: "22px", fontWeight: 900, margin: "6px 0 4px", fontFamily: "ui-monospace, monospace" }}>
              {k.value}
            </p>
            <p style={{ fontSize: "11px", color: "#4b5563" }}>{k.sub}</p>
          </div>
        ))}
      </div>

      <div
        style={{
          marginTop: "30px",
          padding: "16px 20px",
          background: alert.color,
          color: "#fff",
          borderRadius: "6px",
        }}
      >
        <p style={{ fontWeight: 900, fontSize: "12px", letterSpacing: "0.2em", margin: 0 }}>
          NIVEAU D'ALERTE VITALISTE
        </p>
        <p style={{ fontSize: "20px", fontWeight: 900, margin: "4px 0 0" }}>
          {alert.icon} {alert.label}
        </p>
      </div>
    </Page>
  );
}

// ----------------------------------------------------------------------------
// Pages 4-5 — Detailed analysis (table of inputs + computations)
// ----------------------------------------------------------------------------
function DetailedAnalysisPage({ params, results, country, year, pageNum, totalPages }) {
  const currency = params?.currency || "UM";
  const inputRows = [
    ["Dette publique totale", fmtMoney(params?.total_public_debt, currency)],
    ["Dette intérieure", fmtMoney(params?.domestic_debt, currency)],
    ["Dette extérieure", fmtMoney(params?.external_debt, currency)],
    ["Ratio dette/PIB", params?.debt_to_gdp_ratio !== null && params?.debt_to_gdp_ratio !== undefined ? `${fmt(params.debt_to_gdp_ratio, 2)} %` : "—"],
    ["Service annuel", fmtMoney(params?.annual_debt_service, currency)],
    ["Intérêts annuels", fmtMoney(params?.interest_payments, currency)],
    ["Remboursements capital", fmtMoney(params?.principal_repayments, currency)],
    ["Taux d'intérêt moyen", params?.average_interest_rate ? `${fmt(params.average_interest_rate, 2)} %` : "—"],
    ["Maturité moyenne", params?.average_maturity_years ? `${fmt(params.average_maturity_years, 1)} ans` : "—"],
    ["Déficit fiscal annuel", fmtMoney(params?.annual_fiscal_deficit, currency)],
    ["PIB total", fmtMoney(params?.gdp_total, currency)],
    ["Population totale", params?.total_population ? fmt(params.total_population, 0) : "—"],
    ["Population active", params?.active_population ? fmt(params.active_population, 0) : "—"],
    ["Salaire journalier de référence", `${fmt(results?.daily_salary_used, 4)} ${currency}`],
  ];

  const dailySal = results?.daily_salary_used || 0;
  const popActive = results?.active_population_used || 0;
  const tot = params?.total_public_debt || 0;
  const computeRows = [
    {
      id: "D1", name: "Dette par contribuable",
      detail: `${fmt(tot, 0)} / ${fmt(popActive, 0)} = ${fmt(results?.dette_par_tete_um, 2)} ${currency}`,
      result: fmtMoney(results?.dette_par_tete_um, currency),
    },
    {
      id: "D2", name: "Jours de vie de dette",
      detail: `${fmt(results?.dette_par_tete_um, 2)} / ${fmt(dailySal, 4)} = ${fmt(results?.jours_vie_dette, 4)} jours`,
      result: results?.jours_vie_dette ? `${fmt(results.jours_vie_dette, 2)} j` : "—",
    },
    {
      id: "D3", name: "Années de vie",
      detail: `${fmt(results?.jours_vie_dette, 4)} / 365 = ${fmt(results?.annees_vie_dette, 6)} ans`,
      result: results?.annees_vie_dette ? `${fmt(results.annees_vie_dette, 4)} ans` : "—",
    },
    {
      id: "D4", name: "% carrière sacrifié",
      detail: `(${fmt(results?.annees_vie_dette, 4)} / 40) × 100 = ${fmt(results?.pct_carriere_dette, 4)} %`,
      result: results?.pct_carriere_dette ? `${fmt(results.pct_carriere_dette, 3)} %` : "—",
    },
    {
      id: "D5", name: "Service annuel en jours",
      detail: `(${fmt(params?.annual_debt_service, 0)} / ${fmt(popActive, 0)}) / ${fmt(dailySal, 4)} = ${fmt(results?.jours_vie_service_annuel, 4)} j/an`,
      result: results?.jours_vie_service_annuel ? `${fmt(results.jours_vie_service_annuel, 2)} j/an` : "—",
    },
    {
      id: "D7", name: "TAVD",
      detail: `(JVD ${fmt(results?.jours_vie_dette, 2)} / JVF ${fmt(results?.jours_vie_fiscalite, 2)}) × 100 = ${fmt(results?.tavd, 4)} %`,
      result: results?.tavd ? `${fmt(results.tavd, 2)} %` : "—",
    },
  ];

  return (
    <Page country={country} year={year} pageNum={pageNum} totalPages={totalPages}>
      <h2 style={{ fontSize: "22px", fontWeight: 900, margin: 0 }}>Analyse Détaillée</h2>

      <h3 style={{ fontSize: "14px", marginTop: "18px", fontWeight: 800 }}>Paramètres saisis</h3>
      <table style={{ width: "100%", borderCollapse: "collapse", marginTop: "8px" }}>
        <tbody>
          {inputRows.map(([k, v], i) => (
            <tr key={i} style={{ borderBottom: "1px solid #eef2f7" }}>
              <td style={{ padding: "5px 4px", color: "#374151" }}>{k}</td>
              <td style={{ padding: "5px 4px", textAlign: "right", fontFamily: "ui-monospace, monospace" }}>{v}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <h3 style={{ fontSize: "14px", marginTop: "20px", fontWeight: 800 }}>Calculs intermédiaires</h3>
      <table style={{ width: "100%", borderCollapse: "collapse", marginTop: "8px", fontSize: "11px" }}>
        <thead>
          <tr style={{ background: "#f9fafb" }}>
            <th style={{ textAlign: "left", padding: "6px 4px", borderBottom: "1px solid #e5e7eb", color: COLORS.gold }}>ID</th>
            <th style={{ textAlign: "left", padding: "6px 4px", borderBottom: "1px solid #e5e7eb" }}>Indicateur</th>
            <th style={{ textAlign: "left", padding: "6px 4px", borderBottom: "1px solid #e5e7eb" }}>Détail</th>
            <th style={{ textAlign: "right", padding: "6px 4px", borderBottom: "1px solid #e5e7eb" }}>Résultat</th>
          </tr>
        </thead>
        <tbody>
          {computeRows.map((r) => (
            <tr key={r.id} style={{ borderBottom: "1px solid #eef2f7" }}>
              <td style={{ padding: "6px 4px", fontWeight: 800, color: COLORS.gold }}>{r.id}</td>
              <td style={{ padding: "6px 4px" }}>{r.name}</td>
              <td style={{ padding: "6px 4px", fontFamily: "ui-monospace, monospace", color: "#374151", fontSize: "10px" }}>{r.detail}</td>
              <td style={{ padding: "6px 4px", textAlign: "right", fontWeight: 800 }}>{r.result}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Page>
  );
}

// ----------------------------------------------------------------------------
// Pages 6-8 — Visualisations (charts captured from live DOM)
// ----------------------------------------------------------------------------
function ChartsPage({ chartImages, country, year, pageNum, totalPages, range, dataSource }) {
  // chartImages: array of {title, dataUrl}
  // range: e.g. "1-3" or "4-6" — title hint only
  const slice = chartImages.slice(range[0], range[1]);
  return (
    <Page country={country} year={year} pageNum={pageNum} totalPages={totalPages}>
      <h2 style={{ fontSize: "20px", fontWeight: 900, margin: 0 }}>
        Visualisations vitalistes — graphiques {range[0] + 1} à {Math.min(range[1], chartImages.length)}
      </h2>
      <p style={{ marginTop: "4px", color: "#6b7280", fontSize: "11px" }}>
        Données source : {dataSource || "Paramètres saisis dans le formulaire DEBT-VITA™"}
      </p>
      <div style={{ marginTop: "16px", display: "grid", gridTemplateColumns: "1fr", gap: "16px" }}>
        {slice.map((c, i) => (
          <div key={i} style={{ border: "1px solid #e5e7eb", borderRadius: "6px", padding: "10px" }}>
            <p style={{ fontSize: "11px", fontWeight: 800, color: COLORS.blue, margin: 0 }}>
              {c.title}
            </p>
            {c.dataUrl ? (
              <img src={c.dataUrl} alt={c.title} style={{ width: "100%", marginTop: "6px", borderRadius: "4px" }} />
            ) : (
              <p style={{ color: "#9ca3af", fontStyle: "italic" }}>(graphique non capturé)</p>
            )}
          </div>
        ))}
      </div>
    </Page>
  );
}

// ----------------------------------------------------------------------------
// Page 9 — Creditor breakdown
// ----------------------------------------------------------------------------
function CreditorsPage({ results, params, country, year, totalPages, pageNum }) {
  const breakdown = results?.creditor_breakdown || [];
  const currency = params?.currency || "UM";
  if (breakdown.length === 0) return null;
  return (
    <Page country={country} year={year} pageNum={pageNum} totalPages={totalPages}>
      <h2 style={{ fontSize: "22px", fontWeight: 900, margin: 0 }}>Analyse par créancier</h2>
      <p style={{ marginTop: "6px", color: "#555" }}>
        Décomposition de la dette par origine et coût vitaliste annuel des intérêts.
      </p>
      <table style={{ width: "100%", borderCollapse: "collapse", marginTop: "16px", fontSize: "11px" }}>
        <thead>
          <tr style={{ background: COLORS.ink, color: "#fff" }}>
            <th style={{ padding: "6px 8px", textAlign: "left" }}>Créancier</th>
            <th style={{ padding: "6px 8px", textAlign: "left" }}>Type</th>
            <th style={{ padding: "6px 8px", textAlign: "right" }}>Montant</th>
            <th style={{ padding: "6px 8px", textAlign: "right" }}>Part %</th>
            <th style={{ padding: "6px 8px", textAlign: "right" }}>Dette/tête</th>
            <th style={{ padding: "6px 8px", textAlign: "right" }}>Jours vie</th>
            <th style={{ padding: "6px 8px", textAlign: "right" }}>Coût intérêts/jour</th>
          </tr>
        </thead>
        <tbody>
          {breakdown.map((c, i) => (
            <tr key={i} style={{ borderBottom: "1px solid #eef2f7" }}>
              <td style={{ padding: "6px 8px", fontWeight: 700 }}>{c.name}</td>
              <td style={{ padding: "6px 8px" }}>{c.type}</td>
              <td style={{ padding: "6px 8px", textAlign: "right", fontFamily: "ui-monospace, monospace" }}>{fmtMoney(c.amount, currency)}</td>
              <td style={{ padding: "6px 8px", textAlign: "right" }}>{fmt(c.part_pct, 2)} %</td>
              <td style={{ padding: "6px 8px", textAlign: "right", fontFamily: "ui-monospace, monospace" }}>{fmtMoney(c.dette_par_tete, currency)}</td>
              <td style={{ padding: "6px 8px", textAlign: "right" }}>{fmt(c.jours_vie, 1)} j</td>
              <td style={{ padding: "6px 8px", textAlign: "right" }}>{fmt(c.cost_of_interest_per_capita_daily, 4)} {currency}/j</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Page>
  );
}

// ----------------------------------------------------------------------------
// Page 10 — Projection intergénérationnelle
// ----------------------------------------------------------------------------
function ProjectionPage({ results, params, country, year, totalPages, pageNum, projectionImage }) {
  const currency = params?.currency || "UM";
  const horizon = params?.projection_years || 30;
  const proj = results?.projection_data || [];
  // Decennial milestones
  const decades = [];
  for (let i = 9; i < proj.length; i += 10) decades.push(proj[i]);
  if (proj.length > 0 && decades.length === 0) decades.push(proj[proj.length - 1]);

  return (
    <Page country={country} year={year} pageNum={pageNum} totalPages={totalPages}>
      <h2 style={{ fontSize: "22px", fontWeight: 900, margin: 0 }}>Projection intergénérationnelle</h2>
      <p style={{ marginTop: "6px", color: "#555" }}>Horizon : {horizon} ans · {proj.length} années projetées</p>

      {projectionImage ? (
        <img
          src={projectionImage}
          alt="Projection 3 scénarios"
          style={{ width: "100%", marginTop: "12px", border: "1px solid #e5e7eb", borderRadius: "6px" }}
        />
      ) : null}

      <h3 style={{ marginTop: "20px", fontSize: "14px", fontWeight: 800 }}>Tableau par décennie</h3>
      <table style={{ width: "100%", borderCollapse: "collapse", marginTop: "8px", fontSize: "11px" }}>
        <thead>
          <tr style={{ background: "#f9fafb" }}>
            <th style={{ textAlign: "left", padding: "6px 4px" }}>Année</th>
            <th style={{ textAlign: "right", padding: "6px 4px" }}>Dette projetée</th>
            <th style={{ textAlign: "right", padding: "6px 4px" }}>Dette/tête</th>
            <th style={{ textAlign: "right", padding: "6px 4px" }}>Jours vie</th>
            <th style={{ textAlign: "right", padding: "6px 4px" }}>Années vie</th>
          </tr>
        </thead>
        <tbody>
          {decades.map((d) => (
            <tr key={d.annee} style={{ borderBottom: "1px solid #eef2f7" }}>
              <td style={{ padding: "6px 4px", fontWeight: 700 }}>{d.annee}</td>
              <td style={{ padding: "6px 4px", textAlign: "right", fontFamily: "ui-monospace, monospace" }}>{fmtMoney(d.dette_totale, currency)}</td>
              <td style={{ padding: "6px 4px", textAlign: "right" }}>{fmtMoney(d.dette_par_tete, currency)}</td>
              <td style={{ padding: "6px 4px", textAlign: "right" }}>{fmt(d.jours_vie, 1)} j</td>
              <td style={{ padding: "6px 4px", textAlign: "right" }}>{fmt(d.annees_vie, 3)} ans</td>
            </tr>
          ))}
        </tbody>
      </table>

      {results?.annees_vie_enfant ? (
        <p style={{ marginTop: "20px", fontSize: "13px", fontStyle: "italic", borderLeft: `4px solid ${COLORS.gold}`, padding: "8px 12px", background: "#fffbe6" }}>
          Un enfant né aujourd'hui aura {horizon} ans en {new Date().getFullYear() + horizon} et
          portera déjà <strong>{fmt(results.annees_vie_enfant, 2)} années</strong> de dette
          sur ses épaules — sans avoir voté la moindre décision financière.
        </p>
      ) : null}
    </Page>
  );
}

// ----------------------------------------------------------------------------
// Page 11 — Narrative LLM
// ----------------------------------------------------------------------------
function NarrativePage({ narrative, country, year, totalPages, pageNum }) {
  if (!narrative) return null;
  const apg = narrative.analyse_par_generation || {};
  return (
    <Page country={country} year={year} pageNum={pageNum} totalPages={totalPages}>
      <p style={{ fontSize: "10px", letterSpacing: "0.3em", color: COLORS.gold, fontWeight: 700, margin: 0 }}>
        ANALYSE NARRATIVE VITALISTE
      </p>
      <h2 style={{ fontSize: "22px", fontWeight: 900, margin: "6px 0", color: COLORS.blue }}>
        {narrative.titre_analyse}
      </h2>
      <p style={{ fontSize: "13px", lineHeight: 1.6, marginTop: "8px" }}>{narrative.synthese_vitaliste}</p>

      <h3 style={{ fontSize: "13px", marginTop: "16px", fontWeight: 800 }}>Lecture intergénérationnelle</h3>
      <ul style={{ marginTop: "6px", paddingLeft: "18px" }}>
        <li><strong>Génération actuelle —</strong> {apg.generation_actuelle}</li>
        <li><strong>Génération future —</strong> {apg.generation_future}</li>
        <li><strong>Constat —</strong> {apg.constat_intergen}</li>
      </ul>

      {Array.isArray(narrative.points_critiques) && narrative.points_critiques.length > 0 ? (
        <>
          <h3 style={{ fontSize: "13px", marginTop: "14px", fontWeight: 800 }}>Points critiques</h3>
          <ul style={{ marginTop: "6px", paddingLeft: "18px" }}>
            {narrative.points_critiques.map((p, i) => (
              <li key={i} style={{ marginBottom: "4px" }}>⚠ {p}</li>
            ))}
          </ul>
        </>
      ) : null}

      {Array.isArray(narrative.recommandations_vitalistes) && narrative.recommandations_vitalistes.length > 0 ? (
        <>
          <h3 style={{ fontSize: "13px", marginTop: "14px", fontWeight: 800 }}>Recommandations</h3>
          <ol style={{ marginTop: "6px", paddingLeft: "20px" }}>
            {narrative.recommandations_vitalistes.map((r, i) => (
              <li key={i} style={{ marginBottom: "4px" }}>{r}</li>
            ))}
          </ol>
        </>
      ) : null}

      {narrative.citation_ouvrage ? (
        <blockquote style={{ marginTop: "18px", borderLeft: `4px solid ${COLORS.gold}`, padding: "10px 14px", background: "#fffbe6", fontStyle: "italic" }}>
          « {narrative.citation_ouvrage} »
          <footer style={{ marginTop: "4px", fontSize: "10px", color: COLORS.gold, fontWeight: 800 }}>
            — A. ELY Mustapha, Nouvelle Théorie des Finances Publiques
          </footer>
        </blockquote>
      ) : null}

      {narrative.conclusion ? (
        <p style={{ marginTop: "14px", fontSize: "12px" }}>{narrative.conclusion}</p>
      ) : null}
    </Page>
  );
}

// ----------------------------------------------------------------------------
// Page 12 — Conclusions / Recommandations vitalistes (déterministes)
// ----------------------------------------------------------------------------
function ConclusionsPage({ results, params, country, year, totalPages, pageNum }) {
  const ratio = params?.debt_to_gdp_ratio;
  const tavd = results?.tavd;
  const seuils = [
    { rule: "Critère de Maastricht (UE — TFUE art. 126)", threshold: "≤ 60 % PIB",
      ok: ratio !== null && ratio !== undefined ? ratio <= 60 : null,
      val: ratio !== null && ratio !== undefined ? `${fmt(ratio, 1)} %` : "—" },
    { rule: "Seuil d'alerte FMI (DSA)", threshold: "≤ 90 % PIB",
      ok: ratio !== null && ratio !== undefined ? ratio <= 90 : null,
      val: ratio !== null && ratio !== undefined ? `${fmt(ratio, 1)} %` : "—" },
    { rule: "Seuil empirique Reinhart-Rogoff", threshold: "≤ 130 % PIB",
      ok: ratio !== null && ratio !== undefined ? ratio <= 130 : null,
      val: ratio !== null && ratio !== undefined ? `${fmt(ratio, 1)} %` : "—" },
    { rule: "Critère vitaliste TAVD", threshold: "≤ 300 %",
      ok: tavd !== null && tavd !== undefined ? tavd <= 300 : null,
      val: tavd !== null && tavd !== undefined ? `${fmt(tavd, 1)} %` : "—" },
  ];
  return (
    <Page country={country} year={year} pageNum={pageNum} totalPages={totalPages}>
      <h2 style={{ fontSize: "22px", fontWeight: 900, margin: 0 }}>Conclusions & Recommandations</h2>

      <h3 style={{ fontSize: "14px", marginTop: "16px", fontWeight: 800 }}>Seuils de proportionnalité vitale</h3>
      <table style={{ width: "100%", borderCollapse: "collapse", marginTop: "8px", fontSize: "11px" }}>
        <thead>
          <tr style={{ background: "#f9fafb" }}>
            <th style={{ textAlign: "left", padding: "6px 4px" }}>Référence normative</th>
            <th style={{ textAlign: "left", padding: "6px 4px" }}>Seuil</th>
            <th style={{ textAlign: "right", padding: "6px 4px" }}>Valeur observée</th>
            <th style={{ textAlign: "right", padding: "6px 4px" }}>Statut</th>
          </tr>
        </thead>
        <tbody>
          {seuils.map((s, i) => (
            <tr key={i} style={{ borderBottom: "1px solid #eef2f7" }}>
              <td style={{ padding: "6px 4px", fontWeight: 600 }}>{s.rule}</td>
              <td style={{ padding: "6px 4px" }}>{s.threshold}</td>
              <td style={{ padding: "6px 4px", textAlign: "right", fontFamily: "ui-monospace, monospace" }}>{s.val}</td>
              <td style={{ padding: "6px 4px", textAlign: "right", fontWeight: 800,
                color: s.ok === null ? "#888" : s.ok ? COLORS.green : COLORS.red }}>
                {s.ok === null ? "—" : s.ok ? "✓ Conforme" : "✗ Dépassé"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <h3 style={{ fontSize: "14px", marginTop: "16px", fontWeight: 800 }}>Recommandations vitalistes</h3>
      <ol style={{ marginTop: "8px", paddingLeft: "20px", fontSize: "12px", lineHeight: 1.7 }}>
        <li>Inscrire le <strong>poids vitaliste</strong> de la dette comme indicateur permanent dans tout débat budgétaire.</li>
        <li>Plafonner la dette par contribuable actif à la <strong>moitié d'une carrière</strong> (≤ 50 % de PCS).</li>
        <li>Communiquer chaque année le <strong>jour de libération de la dette</strong> aux citoyens (calendrier vital).</li>
        <li>Inclure la <strong>charge sur l'enfant à naître</strong> dans toute loi de finances comme bilan d'ouverture.</li>
        <li>Soumettre tout franchissement de seuil critique à un <strong>vote de proportionnalité vitale</strong>.</li>
      </ol>

      <p style={{ marginTop: "20px", fontSize: "10px", color: "#6b7280", fontStyle: "italic" }}>
        Cette page constitue une lecture théorique vitaliste à fins pédagogiques.
        Elle n'engage ni notation financière, ni avis juridique, ni position institutionnelle.
      </p>
    </Page>
  );
}

// ----------------------------------------------------------------------------
// Annex — Glossary & sources
// ----------------------------------------------------------------------------
function AnnexPage({ params, country, year, totalPages, pageNum }) {
  return (
    <Page country={country} year={year} pageNum={pageNum} totalPages={totalPages}>
      <h2 style={{ fontSize: "22px", fontWeight: 900, margin: 0 }}>Annexes</h2>

      <h3 style={{ fontSize: "14px", marginTop: "14px", fontWeight: 800 }}>Glossaire</h3>
      <dl style={{ marginTop: "6px", fontSize: "11px" }}>
        <dt style={{ fontWeight: 800 }}>JVD — Jours de Vie de Dette</dt>
        <dd style={{ marginLeft: "12px", marginBottom: "6px" }}>Nombre de jours qu'un contribuable actif moyen doit à la dette par tête.</dd>
        <dt style={{ fontWeight: 800 }}>AVH — Années de Vie Hypothéquées</dt>
        <dd style={{ marginLeft: "12px", marginBottom: "6px" }}>JVD divisé par 365.</dd>
        <dt style={{ fontWeight: 800 }}>PCS — Pourcentage de Carrière Sacrifié</dt>
        <dd style={{ marginLeft: "12px", marginBottom: "6px" }}>AVH rapporté à la durée de la vie active (40 ans par défaut).</dd>
        <dt style={{ fontWeight: 800 }}>SAV — Service Annuel en Vies</dt>
        <dd style={{ marginLeft: "12px", marginBottom: "6px" }}>Jours de vie consommés chaque année rien que pour servir la dette.</dd>
        <dt style={{ fontWeight: 800 }}>TAVD — Taux d'Aliénation Vitaliste de la Dette</dt>
        <dd style={{ marginLeft: "12px", marginBottom: "6px" }}>Ratio JVD / JVF en %, mesure du déséquilibre entre dette et fiscalité courante.</dd>
        <dt style={{ fontWeight: 800 }}>CEB — Charge sur l'Enfant à Naître</dt>
        <dd style={{ marginLeft: "12px", marginBottom: "6px" }}>Projection de la dette par tête à un horizon donné.</dd>
      </dl>

      <h3 style={{ fontSize: "14px", marginTop: "14px", fontWeight: 800 }}>Sources des données utilisées</h3>
      <p style={{ marginTop: "4px", fontSize: "11px" }}>
        {params?.data_source || "Aucune source explicite saisie par l'utilisateur."}
      </p>

      <h3 style={{ fontSize: "14px", marginTop: "14px", fontWeight: 800 }}>Référence fondatrice</h3>
      <p style={{ marginTop: "4px", fontSize: "11px" }}>
        ELY Mustapha (A.) — <em>Nouvelle Théorie des Finances Publiques. Le poids
        vitaliste de l'État.</em> Méthodologie originale convertissant la fiscalité
        et la dette publique en équivalents de temps de vie humaine.
      </p>

      <p style={{ marginTop: "30px", fontSize: "9px", color: "#6b7280", fontStyle: "italic" }}>
        Rapport généré automatiquement par VITAE-PUBLICA™ DEBT-VITA™ — module d'analyse
        développé par ELY Mustapha (PMP · I-PMP · IBM Full Stack Developer).
      </p>
    </Page>
  );
}

// ============================================================================
// Main template
// ============================================================================
export default function DebtReportTemplate({
  project,
  params,
  results,
  narrative,
  chartImages = [],
  projectionImage,
  generatedAt,
}) {
  const country = project?.country || params?.country || "Pays non spécifié";
  const year = params?.fiscal_year || project?.fiscal_year || "";
  const dataSource = params?.data_source;

  // Page numbering : we know counts only after rendering; precompute estimate.
  const hasCreditors = (results?.creditor_breakdown || []).length > 0;
  const hasNarrative = !!narrative;
  let pageNo = 1;
  const total = 7 + (hasCreditors ? 1 : 0) + (hasNarrative ? 1 : 0); // approx
  const next = () => ++pageNo;

  return (
    <div
      id="debt-report-template-root"
      style={{
        position: "fixed",
        left: "-10000px",
        top: 0,
        background: "#fff",
        width: "794px",
      }}
    >
      <CoverPage project={project} params={params} results={results} generatedAt={generatedAt} totalPages={total} />
      <MethodologyPage country={country} year={year} totalPages={total} />
      <ExecSummaryPage results={results} params={params} country={country} year={year} totalPages={total} />
      <DetailedAnalysisPage params={params} results={results} country={country} year={year} pageNum={4} totalPages={total} />

      {/* Charts (3 pages of ~3 charts each = 9 charts max) */}
      {chartImages.length > 0 ? (
        <ChartsPage chartImages={chartImages} range={[0, 3]} country={country} year={year} pageNum={5} totalPages={total} dataSource={dataSource} />
      ) : null}
      {chartImages.length > 3 ? (
        <ChartsPage chartImages={chartImages} range={[3, 6]} country={country} year={year} pageNum={6} totalPages={total} dataSource={dataSource} />
      ) : null}
      {chartImages.length > 6 ? (
        <ChartsPage chartImages={chartImages} range={[6, 9]} country={country} year={year} pageNum={7} totalPages={total} dataSource={dataSource} />
      ) : null}

      {hasCreditors ? (
        <CreditorsPage results={results} params={params} country={country} year={year} pageNum={8} totalPages={total} />
      ) : null}

      <ProjectionPage results={results} params={params} country={country} year={year} pageNum={hasCreditors ? 9 : 8} totalPages={total} projectionImage={projectionImage} />

      {hasNarrative ? (
        <NarrativePage narrative={narrative} country={country} year={year} pageNum={hasCreditors ? 10 : 9} totalPages={total} />
      ) : null}

      <ConclusionsPage results={results} params={params} country={country} year={year} pageNum={total - 1} totalPages={total} />
      <AnnexPage params={params} country={country} year={year} pageNum={total} totalPages={total} />
    </div>
  );
}
